import React, { useCallback, useEffect, useMemo } from "react";
import {
  Grid,
  TextField,
  Typography,
  //Checkbox,
  //FormControlLabel,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { useFormikContext } from "formik";
import { getFieldProps, inputProps } from "../../utils";
import InfoTooltip from "../../InfoTooltip";
import ImageUpload from "../../ImageUpload";
import { useAppContext } from "../../../api/AppContext";
import {
  getCompletionState,
  getLocalizedField,
  getNewsFieldByLanguage,
  isTagNameCompleteForSave,
  LANGUAGE_LABELS,
  SUPPORTED_LANGUAGES,
} from "../../shared/languageConfig";
import LanguageSelector from "../../shared/LanguageSelector";
import { usePreferredLanguagePersistence } from "../../shared/usePreferredLanguagePersistence";
//import UserStatePathSelect from "../../Interaction/UserStatePathSelect";

// hacky way to allow file upload for Allitude
const ALLITUDE_COMPANY_ID = "21330221-0072-45a1-88e1-d6ad6de3bf40";
const getCustomPathInfo = (user) => {
  if (user.companies?.includes(ALLITUDE_COMPANY_ID)) {
    return {
      journeyId:
        process.env.REACT_APP_DASHBOARD_STAGE === "qa"
          ? "afb4b4d1-26de-40fe-b336-fc5167b76204"
          : "7b9743a2-fae7-4a03-95a1-0d62bfe1839a",
    };
  } else {
    return { cid: "67df6292-c0a7-4831-a94e-61b6ea73c990" };
  }
};

const InitialInformation = () => {
  const formik = useFormikContext();
  const {
    //userStatePaths,
    //isUserStatePathsLoading,
    fetchUserStatePaths,
    user,
    newsTags,
    fetchNewsTags,
  } = useAppContext();

  const customPathInfo = getCustomPathInfo(user);
  const persistPreferredLanguage = usePreferredLanguagePersistence();

  useEffect(() => {
    fetchUserStatePaths();
  }, [fetchUserStatePaths]);

  useEffect(() => {
    fetchNewsTags();
  }, [fetchNewsTags]);

  const selectedTags = useMemo(() => {
    const ids = formik.values.tagIds || [];
    return (newsTags || []).filter((t) => ids.includes(t.id));
  }, [formik.values.tagIds, newsTags]);

  const validLanguages = formik.values.validLanguages || ["it", "en"];
  const preferredLanguage =
    formik.values.uiPreferredLanguage || validLanguages[0];
  const secondaryLanguages = validLanguages.filter(
    (language) => language !== preferredLanguage
  );
  const activeSecondaryLanguage =
    formik.values.uiActiveSecondaryLanguage || secondaryLanguages[0] || null;

  const sortedNewsTagOptions = useMemo(() => {
    const tags = newsTags || [];

    const hasAnyLanguageName = (tag) =>
      SUPPORTED_LANGUAGES.some((language) =>
        isTagNameCompleteForSave(tag.tagname, language)
      );

    const getSortLabel = (tag) => {
      const preferredLabel = getLocalizedField(tag.tagname, preferredLanguage);
      if (preferredLabel) {
        return preferredLabel;
      }

      return (
        SUPPORTED_LANGUAGES.map((language) =>
          getLocalizedField(tag.tagname, language)
        ).find(Boolean) || ""
      );
    };

    const preferredLanguageTags = tags
      .filter((tag) => isTagNameCompleteForSave(tag.tagname, preferredLanguage))
      .sort((a, b) => getSortLabel(a).localeCompare(getSortLabel(b)));

    const otherLanguageTags = tags
      .filter(
        (tag) =>
          hasAnyLanguageName(tag) &&
          !isTagNameCompleteForSave(tag.tagname, preferredLanguage)
      )
      .sort((a, b) => getSortLabel(a).localeCompare(getSortLabel(b)));

    return [...preferredLanguageTags, ...otherLanguageTags];
  }, [newsTags, preferredLanguage]);

  const selectableNewsTagOptions = useMemo(() => {
    const selectedTagById = new Map(
      (selectedTags || []).map((tag) => [tag.id, tag])
    );
    const optionById = new Map(
      (sortedNewsTagOptions || []).map((tag) => [tag.id, tag])
    );

    selectedTagById.forEach((tag, id) => {
      if (!optionById.has(id)) {
        optionById.set(id, tag);
      }
    });

    return Array.from(optionById.values());
  }, [selectedTags, sortedNewsTagOptions]);

  const languageCompletionByCode = useMemo(() => {
    return validLanguages.reduce((acc, language) => {
      const fieldName = getNewsFieldByLanguage(language);
      const completed = ["title", "subtitle"].reduce((total, field) => {
        return total + (formik.values?.[fieldName]?.[field]?.trim() ? 1 : 0);
      }, 0);

      acc[language] = getCompletionState({
        completedLanguage: completed,
        isActive: language === activeSecondaryLanguage,
        totalRequiredFields: 2,
      });
      return acc;
    }, {});
  }, [activeSecondaryLanguage, formik.values, validLanguages]);

  const buildTextField = (
    formik,
    placeholder,
    name,
    label,
    required = false
  ) => {
    return (
      <>
        <TextField
          fullWidth
          InputLabelProps={{ shrink: true }}
          InputProps={inputProps}
          label={label}
          margin="none"
          placeholder={placeholder}
          required={required}
          variant="outlined"
          {...getFieldProps(formik, {
            name,
          })}
        />
      </>
    );
  };

  const handleImageChange = useCallback(
    ({ url }) => {
      formik.setFieldValue(`image`, url);
      formik.setFieldTouched(`image`, true);
    },
    [formik]
  );

  const handleImageReset = useCallback(() => {
    formik.setFieldValue("image", null);
    formik.setFieldTouched("image", true);
  }, [formik]);

  return (
    <Grid item xs={12}>
      <Grid item>
        <Typography variant="h6">
          In this section you will need to upload the appearance of your news!
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <Grid item xs={12}>
          <Grid item style={{ marginBottom: "1rem", marginTop: "1rem" }}>
            <Typography variant="h6">
              The headline of the news ! Be sure to make it engaging and
              intuitive !
            </Typography>
          </Grid>
          <Grid container spacing={2}>
            <LanguageSelector
              languageCompletionByCode={languageCompletionByCode}
              layout="split"
              preferredLanguage={preferredLanguage}
              validLanguages={validLanguages}
              onActiveSecondaryLanguageChange={(language) =>
                formik.setFieldValue("uiActiveSecondaryLanguage", language)
              }
              onPreferredLanguageChange={(nextPreferred, nextSecondary) => {
                formik.setFieldValue("uiPreferredLanguage", nextPreferred);
                formik.setFieldValue(
                  "uiActiveSecondaryLanguage",
                  nextSecondary
                );
                persistPreferredLanguage(nextPreferred);
              }}
            />
            <Grid item md={secondaryLanguages.length ? 6 : 12} xs={12}>
              {buildTextField(
                formik,
                "Write news headline...",
                `${getNewsFieldByLanguage(preferredLanguage)}.title`,
                LANGUAGE_LABELS[preferredLanguage] || preferredLanguage,
                true
              )}
            </Grid>
            {activeSecondaryLanguage && (
              <Grid item md={6} xs={12}>
                {buildTextField(
                  formik,
                  "Write news headline...",
                  `${getNewsFieldByLanguage(activeSecondaryLanguage)}.title`,
                  LANGUAGE_LABELS[activeSecondaryLanguage] ||
                    activeSecondaryLanguage
                )}
              </Grid>
            )}
          </Grid>

          <Grid
            container
            spacing={2}
            style={{ marginBottom: "1rem", marginTop: "1rem" }}
          >
            <Grid item md={secondaryLanguages.length ? 6 : 12} xs={12}>
              {buildTextField(
                formik,
                "Write a subheading...",
                `${getNewsFieldByLanguage(preferredLanguage)}.subtitle`,
                LANGUAGE_LABELS[preferredLanguage] || preferredLanguage,
                true
              )}
            </Grid>
            {activeSecondaryLanguage && (
              <Grid item md={6} xs={12}>
                {buildTextField(
                  formik,
                  "Write a subheading...",
                  `${getNewsFieldByLanguage(activeSecondaryLanguage)}.subtitle`,
                  LANGUAGE_LABELS[activeSecondaryLanguage] ||
                    activeSecondaryLanguage
                )}
              </Grid>
            )}
          </Grid>
          <Grid item style={{ marginBlock: "1rem" }} xs={6}>
            <Typography gutterBottom variant="h6">
              Help readers find it! Add tags
            </Typography>
            <Autocomplete
              filterSelectedOptions
              multiple
              getOptionLabel={(option) =>
                getLocalizedField(option.tagname, preferredLanguage) ||
                SUPPORTED_LANGUAGES.map((language) =>
                  getLocalizedField(option.tagname, language)
                ).find(Boolean) ||
                ""
              }
              isOptionEqualToValue={(option, value) => option.id === value.id}
              options={selectableNewsTagOptions}
              renderInput={(params) => (
                <TextField
                  {...params}
                  InputLabelProps={{ shrink: true }}
                  label="Select tags"
                  margin="none"
                  placeholder="Select tag(s)..."
                  variant="outlined"
                />
              )}
              value={selectedTags}
              onChange={(_, value) => {
                formik.setFieldValue(
                  "tagIds",
                  (value || []).map((v) => v.id)
                );
                formik.setFieldTouched("tagIds", true);
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <Grid container alignItems="center" spacing={1}>
              <Grid item>
                <Typography variant="h6">
                  {" "}
                  Finally, upload the cover image!
                </Typography>
              </Grid>
              <Grid item>
                <InfoTooltip
                  text="When uploading a cover image, the recommended dimensions are 880 x 490 px.
                        Please note that the visible area in the various modules will always be 880 x 434 px.
                        Images with proportional (multiple) dimensions are also acceptable, as long as they maintain the same aspect ratio."
                  title="Image Dimensions"
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item md={6} xs={12}>
            <ImageUpload
              buttonProps={{
                children: "Upload",
              }}
              caption={
                <Typography
                  color="textSecondary"
                  style={{ color: "#BFBFBF" }}
                  variant="body2"
                >
                  Upload news cover image (.png, .jpg, .jpeg)
                </Typography>
              }
              containerStyles={{ height: 250 }}
              customPathInfo={customPathInfo}
              errorMessage={getFieldProps(formik, { name: "image" }).helperText}
              maxSize={15 * 1024 * 1024} // 15 MB
              styles={{ maxHeight: 250, width: "unset" }}
              value={formik.values.image}
              onChange={handleImageChange}
              onReset={handleImageReset}
            />
          </Grid>

          {/*<Grid item style={{ marginTop: "2rem" }} xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formik.values.conditions.allowSubscription || false}
                  color="primary"
                  onChange={(e) => {
                    formik.setFieldValue(
                      "conditions.allowSubscription",
                      e.target.checked
                    );
                  }}
                />
              }
              label="Let the users subscribe to the news"
            />
          </Grid>*/}

          {/*{formik.values.conditions.allowSubscription && (
            <Grid item style={{ marginTop: "1rem" }} xs={4}>
              <UserStatePathSelect
                required
                isLoading={isUserStatePathsLoading}
                label="Field assigned"
                userStatePaths={userStatePaths}
                value={formik.values.conditions.assignedField}
                onChange={(value) => {
                  formik.setFieldValue("conditions.assignedField", value);
                  formik.setFieldTouched("conditions.assignedField", true);
                }}
              />
            </Grid>
          )}*/}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default InitialInformation;
